import { useCallback, useRef, useState } from 'react';

/**
 * LEADS1 - a second-tap confirm that a DOUBLE TAP cannot pass.
 *
 * Joseph works from his phone, and a double tap is two clicks ~100-300 ms apart. A confirm that renders where the
 * first tap landed is answered by the second half of the same gesture, so a two-tap guard alone guards nothing. Two
 * defences, used together by every caller:
 *   1. the confirm is its OWN row with the SAFE choice first and holding focus (the caller renders that);
 *   2. the dangerous button IGNORES taps until the confirm has been on screen CONFIRM_GUARD_MS - `armed()` below.
 *
 * ⭐ A TIMESTAMP, NOT A TIMER. Nothing waits or re-renders when the guard expires: a tap simply checks the clock, so a
 *    test moves an injected `now` instead of sleeping, and an early tap is ignored rather than taken as "no".
 */
export const CONFIRM_GUARD_MS = 600;

export function useConfirmGuard(now: () => number = () => Date.now(), guardMs: number = CONFIRM_GUARD_MS) {
  const [open, setOpen] = useState(false);
  const shownAt = useRef(0);
  const ask = () => {
    shownAt.current = now();
    setOpen(true);
  };
  // Stable, so a caller can close the confirm from inside its own effects and callbacks.
  const back = useCallback(() => setOpen(false), []);
  /** True only once the confirm has been on screen for the whole guard. */
  const armed = () => open && now() - shownAt.current >= guardMs;
  return { open, ask, back, armed };
}

/**
 * The same guard from the moment a control APPEARS. ⛔ A double tap on "Save edit": the save reloads the queue, the card
 * remounts (keyed by version) and its Approve sits in the slot Save edit was in - the second half of the tap lands on
 * it. So a freshly mounted card's Approve ignores taps for CONFIRM_GUARD_MS too. Returns `armed()`, read at tap time.
 */
export function useArmedAfterMount(now: () => number = () => Date.now(), guardMs: number = CONFIRM_GUARD_MS) {
  const [mountedAt] = useState(now);
  return () => now() - mountedAt >= guardMs;
}
